import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { KeyRound, AlertCircle } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
  userName?: string | null;
}

export function SetTemporaryPasswordDialog({ open, onOpenChange, userId, userName }: Props) {
  const { toast } = useToast();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) { setPw(""); setConfirm(""); setError(null); setSubmitting(false); }
  }, [open]);

  const handleSubmit = async () => {
    setError(null);
    if (!userId) { setError("Missing user reference"); return; }
    if (pw.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { setError("Passwords do not match"); return; }

    setSubmitting(true);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/admin-set-temporary-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ user_id: userId, password: pw }),
        }
      );
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Failed to set password");

      toast({
        title: "Temporary password set",
        description: `Share it with ${userName ?? "the user"} and ask them to change it after login.`,
      });
      onOpenChange(false);
    } catch (e: any) {
      setError(e.message || "Failed to set password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" /> Set Temporary Password
          </DialogTitle>
          <DialogDescription>
            Set a temporary login password for <strong>{userName ?? "this user"}</strong>. Share it
            with them privately and ask them to change it after signing in.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="tp-pw">New password</Label>
            <Input id="tp-pw" type="text" autoComplete="off" value={pw}
              onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="tp-confirm">Confirm password</Label>
            <Input id="tp-confirm" type="text" autoComplete="off" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} placeholder="Re-type the password" />
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || !pw || !confirm}>
            {submitting ? "Saving..." : "Set Password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
